import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Text,
  Title,
  Stack,
  Group,
  ThemeIcon,
} from '@mantine/core';
import { Loader } from '../../components/common/Loader';
import { Helmet } from 'react-helmet-async';
import {
  IconMapPin,
  IconPhone,
  IconMail,
  IconBrandWhatsapp,
  IconCheck,
} from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import { SectionTitle } from '../../components/common/SectionTitle';
import { InquiryForm } from '../../components/forms/InquiryForm';
import { notifications } from '@mantine/notifications';
import { InquiryFormValues } from '../../types';
import axios from 'axios';
import { useSEO } from '../../hooks/useSEO';

const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://thalvora-exports.onrender.com';

export const ContactPage: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);

  const contact = content.pages.contact;

  const { seo, loading } = useSEO('contact');

  const [submitting, setSubmitting] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  // 🔥 Cached contact info
  const [cachedContact, setCachedContact] =
    useState(contact);

  // 🔥 Cache contact data for instant loading
  useEffect(() => {
    const cached = sessionStorage.getItem(
      'contact_page_data'
    );

    if (cached && cached !== 'undefined') {
      try {
        const parsed = JSON.parse(cached);

        setCachedContact(parsed);
      } catch (e) {
        console.error('Cache parse error:', e);
      }
    }

    // Save latest contact data
    if (contact) {
      setCachedContact(contact);

      sessionStorage.setItem(
        'contact_page_data',
        JSON.stringify(contact)
      );
    }
  }, [contact]);

  const handleInquirySubmit = async (
    values: InquiryFormValues
  ) => {
    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_URL}/inquiries`,
        values
      );

      if (response.data) {
        setSubmitted(true);

        notifications.show({
          title: 'Success!',
          message:
            'Thank you for contacting us. We will respond within 24 hours.',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }
    } catch (error: any) {
      console.error(
        'Error submitting inquiry:',
        error
      );

      notifications.show({
        title: 'Error',
        message:
          error.response?.data?.message ||
          'Failed to submit inquiry. Please try again.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: IconMapPin,
      label: 'Address',
      value: cachedContact?.address,
    },
    {
      icon: IconPhone,
      label: 'Phone',
      value: cachedContact?.phone,
    },
    {
      icon: IconMail,
      label: 'Email',
      value: cachedContact?.email,
    },
    {
      icon: IconBrandWhatsapp,
      label: 'WhatsApp',
      value: cachedContact?.whatsapp,
    },
  ];

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>

        <meta
          name="description"
          content={seo?.metaDescription}
        />

        <meta
          name="keywords"
          content={seo?.metaKeywords?.join(', ')}
        />

        <meta
          property="og:title"
          content={seo?.ogTitle}
        />

        <meta
          property="og:description"
          content={seo?.ogDescription}
        />

        {seo?.ogImage && (
          <meta
            property="og:image"
            content={seo.ogImage}
          />
        )}

        <meta
          name="twitter:title"
          content={seo?.twitterTitle}
        />

        <meta
          name="twitter:description"
          content={seo?.twitterDescription}
        />

        {seo?.twitterImage && (
          <meta
            name="twitter:image"
            content={seo.twitterImage}
          />
        )}

        {seo?.canonicalUrl && (
          <link
            rel="canonical"
            href={seo.canonicalUrl}
          />
        )}
      </Helmet>

      <Container size="xl">
        <SectionTitle
          title={cachedContact?.title}
          subtitle={cachedContact?.description}
        />

        <Grid gutter={30}>
          {/* LEFT */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Paper withBorder p="xl" radius="lg">
              <Title order={3} mb="lg">
                Get in Touch
              </Title>

              <Stack gap="lg">
                {contactInfo.map((item) => (
                  <Group
                    key={item.label}
                    align="flex-start"
                  >
                    <ThemeIcon
                      color="primary"
                      variant="light"
                      size="lg"
                    >
                      <item.icon size={20} />
                    </ThemeIcon>

                    <div style={{ flex: 1 }}>
                      <Text size="sm" c="dimmed">
                        {item.label}
                      </Text>

                      <Text fw={500}>
                        {item.value}
                      </Text>
                    </div>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Grid.Col>

          {/* RIGHT */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Paper withBorder p="xl" radius="lg">
              <Title order={3} mb="lg">
                Send us a Message
              </Title>

              {submitted ? (
                <div className="text-center py-8">
                  <ThemeIcon
                    size={80}
                    radius="xl"
                    color="green"
                    variant="light"
                    className="mx-auto mb-4"
                  >
                    <IconCheck size={40} />
                  </ThemeIcon>

                  <Title order={3} mb="sm">
                    Thank You!
                  </Title>

                  <Text c="dimmed" mb="lg">
                    Your inquiry has been submitted
                    successfully. Our team will get
                    back to you within 24 hours.
                  </Text>

                  <button
                    onClick={() =>
                      setSubmitted(false)
                    }
                    className="px-6 py-2 rounded-md transition-colors"
                    style={{
                      backgroundColor:
                        'var(--brand-primary)',
                      color: 'white',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.opacity =
                        '0.9')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.opacity =
                        '1')
                    }
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <InquiryForm
                  onSubmit={handleInquirySubmit}
                  submitting={submitting}
                />
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
};